import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import queryString from 'query-string'
import { Dropdown } from 'react-bootstrap'
import { CalendarSVG } from 'src/components/Icons/Basic'
import { FlexRow, FlexAlign } from 'src/components/Reusable/Flex'
import { useNavigateContext } from 'src/components/Reusable/Navigation/NavigateContext'
import { FilterParam } from '../../FilterBar.types'
import { SimpleDropdownItems } from '../FilterDropdown'
import { DropdownItemType } from '../FilterDropdown.constants'
import { DropDownMenuWithNesting } from '../FilterDropdown.styles'
import { FilterDropdownToggle } from '../../FilterBar.styles'
import { DashboardDateRangeDefaults } from './DashboardDateRangeFilterDropdown/DashboardDateRangeFilterDropdown.types'
import {
  DashboardDateRangeDefaultsLabel,
  ALL_ACTIVITIES_VALUE,
  ALL_ACTIVITIES_LABEL,
} from './DashboardDateRangeFilterDropdown/DashboardDateRangeFilterDropdown.constants'

interface OverviewDashboardDateRangeFilterDropdownProps {
  // When true, appends an "All Activities" option that applies no date-range limit.
  includeAllTime?: boolean
  // The preset shown when no range is applied. Defaults to LAST_7_DAYS (Overview Dashboard);
  // the Activities page passes LAST_90_DAYS.
  defaultRange?: DashboardDateRangeDefaults
}

// Preset-only date range filter for the Overview Dashboard — no custom range picker.
export const OverviewDashboardDateRangeFilterDropdown = React.memo(
  ({
    includeAllTime = false,
    defaultRange = DashboardDateRangeDefaults.LAST_7_DAYS,
  }: OverviewDashboardDateRangeFilterDropdownProps = {}): JSX.Element => {
    const location = useLocation()
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const searchParamsFallback = useMemo(
      () => queryString.parse(location.search, { arrayFormat: 'bracket' }),
      [location],
    )
    const {
      navigateState: { parsedQueryParams },
    } = useNavigateContext()
    const searchParams = parsedQueryParams || searchParamsFallback

    const defaultSet = useMemo(() => {
      const values: string[] = Array.from(
        Object.values(DashboardDateRangeDefaults),
      )
      if (includeAllTime) values.push(ALL_ACTIVITIES_VALUE)
      return new Set(values)
    }, [includeAllTime])

    const appliedFilters = useMemo((): Record<string, string[]> => {
      const preprocessed = searchParams[FilterParam.DASHBOARD_DATE_RANGE]
      if (!preprocessed) return {}
      const value = Array.isArray(preprocessed) ? preprocessed[0] : preprocessed
      if (value && defaultSet.has(value as DashboardDateRangeDefaults)) {
        return { [FilterParam.DASHBOARD_DATE_RANGE]: [value] }
      }
      return {}
    }, [searchParams, defaultSet])

    useEffect(() => {
      setDropdownOpen(false)
    }, [location.search])

    const filterValues: DropdownItemType[] = useMemo(() => {
      const values: DropdownItemType[] = [
        { value: DashboardDateRangeDefaults.LAST_7_DAYS, label: 'Last 7 Days' },
        {
          value: DashboardDateRangeDefaults.LAST_30_DAYS,
          label: 'Last 30 Days',
        },
        {
          value: DashboardDateRangeDefaults.LAST_90_DAYS,
          label: 'Last 90 Days',
        },
        { value: DashboardDateRangeDefaults.LAST_YEAR, label: 'Last Year' },
      ]
      if (includeAllTime) {
        values.push({
          value: ALL_ACTIVITIES_VALUE,
          label: ALL_ACTIVITIES_LABEL,
        })
      }
      return values
    }, [includeAllTime])

    const appliedValue = appliedFilters[FilterParam.DASHBOARD_DATE_RANGE]?.[0]
    const toggleText =
      appliedValue === ALL_ACTIVITIES_VALUE
        ? ALL_ACTIVITIES_LABEL
        : DashboardDateRangeDefaultsLabel[
            appliedValue as DashboardDateRangeDefaults
          ] ?? DashboardDateRangeDefaultsLabel[defaultRange]

    return (
      <Dropdown show={dropdownOpen} onToggle={setDropdownOpen}>
        <Dropdown.Toggle
          as={FilterDropdownToggle}
          id="overview-date-range-filter"
        >
          <FlexRow align={FlexAlign.CENTER} gap={10}>
            <CalendarSVG width={16} height={16} />
            {toggleText}
          </FlexRow>
        </Dropdown.Toggle>
        <DropDownMenuWithNesting>
          <SimpleDropdownItems
            filterParam={FilterParam.DASHBOARD_DATE_RANGE}
            filterValues={filterValues}
            location={location}
            searchParams={searchParams}
            appliedFilters={appliedFilters}
            replaceParam
          />
        </DropDownMenuWithNesting>
      </Dropdown>
    )
  },
)
